/**
 * The PE3D games screen's lists, worked out from what the server answers
 * (accountSettingsComponents/pe3dGamesPanel.js). Pure, so Jest can run them.
 *
 * Every game is a package of one scope, so the screen never asks for a scope:
 * it lists what the registry holds and takes a bare game name where a name has
 * to be typed.
 */

export const PE3D_PACKAGE_SCOPE = "@pe3d-games";

/** `@pe3d-games/kingshot` → `kingshot`. */
export const gameNameOf = (packageName) => String(packageName || "").replace(`${PE3D_PACKAGE_SCOPE}/`, "");

/**
 * What was typed in the search box, as a package name: the scope is added, or
 * taken as given when it was pasted along. null when it cannot be a game name.
 */
export function packageOfQuery(query) {
  const name = gameNameOf(String(query || "").trim().toLowerCase());
  return /^[a-z0-9][a-z0-9._-]*$/.test(name) ? `${PE3D_PACKAGE_SCOPE}/${name}` : null;
}

/** Semver order for x.y.z(-pre), as the server's registry.js: a pre-release sorts before its release. */
export function compareVersions(a, b) {
  const parse = (version) => {
    const [core, pre] = String(version).split("-", 2);
    return { nums: core.split(".").map((n) => parseInt(n, 10) || 0), pre: pre || null };
  };
  const x = parse(a);
  const y = parse(b);
  for (let i = 0; i < 3; i++) if (x.nums[i] !== y.nums[i]) return x.nums[i] - y.nums[i];
  if (x.pre === y.pre) return 0;
  if (!x.pre) return 1;
  if (!y.pre) return -1;
  return x.pre < y.pre ? -1 : 1;
}

/**
 * One row per game: the imported ones and the ones the registry holds, joined
 * on the package name.
 *
 * @param games             `pe3d/catalog/adminGames`
 * @param registryPackages  `pe3d/catalog/registryPackages`; anything but an array = not listed
 * @param lookedUp          packages found by name that the listing did not give
 * @returns {Array<{package, name, title, game, latest, newVersions}>} by title
 */
export function catalogRows(games, registryPackages, lookedUp = []) {
  const rows = new Map();
  for (const game of games || []) {
    rows.set(game.package, { package: game.package, name: gameNameOf(game.package), title: game.title, game, latest: null, newVersions: 0 });
  }
  for (const item of [...(Array.isArray(registryPackages) ? registryPackages : []), ...lookedUp]) {
    const row = rows.get(item.name) || { package: item.name, name: gameNameOf(item.name), title: gameNameOf(item.name), game: null };
    rows.set(item.name, { ...row, latest: item.latest || null, newVersions: item.newVersions || 0 });
  }
  return [...rows.values()].sort((a, b) => String(a.title).localeCompare(String(b.title)));
}

/** The rows a search keeps: the title or the game name contains it. */
export function filterCatalogRows(rows, query) {
  const needle = gameNameOf(String(query || "").trim().toLowerCase());
  if (!needle) return rows;
  return rows.filter((row) => String(row.title).toLowerCase().includes(needle) || row.name.includes(needle));
}

/**
 * A game's versions, newest first: what the registry has, each with the facts
 * of its import when it has been imported. A release that is imported but no
 * longer in the registry is kept, marked `inRegistry: false`.
 *
 * @param registryVersions  `pe3d/catalog/registryVersions` versions, or null when the registry was not read
 * @param importedReleases  the game's `releases` from adminGames
 */
export function releaseRows(registryVersions, importedReleases) {
  const imported = new Map((importedReleases || []).map((release) => [release.version, release]));
  const rows = (registryVersions || []).map((item) => ({
    version: item.version,
    publishedAt: item.publishedAt || null,
    inRegistry: true,
    release: imported.get(item.version) || null,
  }));
  const listed = new Set(rows.map((row) => row.version));
  for (const release of importedReleases || []) {
    if (!listed.has(release.version)) rows.push({ version: release.version, publishedAt: null, inRegistry: registryVersions === null, release });
  }
  return rows.sort((a, b) => compareVersions(b.version, a.version));
}
